import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import ReportTable from "../../common/ReportTable/ReportTable";
import DateRangeFilter from "../../common/DateRangeFilter";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import { useUserData } from "../../../hooks/useUserData";
import { fetchStaffLookup } from "../../../services/lookupService";
import { fetchDocumentsForPublish } from "./qmsService";
import { apiMessage, isApiSuccess } from "./qmsApiUtils";
import { getQmsAccessState } from "./qmsPermissionUtils";
import { getQmsScreen } from "./qmsScreenConfig";
import {
  DEFAULT_PUBLISH_COLUMNS,
  PUBLISH_FIELD_OPTIONS,
  canOpenPublishWorkspace,
  filterPublishReadyDocuments,
  getPublishDocumentsList,
  getPublishFieldLabel,
} from "./qmsPublishUtils";
import {
  buildStaffByIdMap,
  getVaultDocumentDisplayName,
  getVaultDocumentListId,
  getVaultStageClass,
  resolveVaultDocumentStage,
} from "./qmsVaultUtils";
import {
  QMS_COLUMN_DATE_PRESETS,
  QMS_EMPTY_DATE_RANGE,
  matchesQmsDateRange,
} from "./qmsColumnDateFilterUtils";
import BadgeMultiSelect from "../../common/BadgeMultiSelect/BadgeMultiSelect";
import QmsVaultStaffAvatar from "./QmsVaultStaffAvatar";
import QmsPublishWorkspaceModal from "./QmsPublishWorkspaceModal";
import "./QmsPages.scss";

const screen = getQmsScreen("publishDocuments");
const VAULT_CELL_MAX_LEN = 20;

function truncateVaultText(text, maxLen = VAULT_CELL_MAX_LEN) {
  const value = String(text ?? "").trim() || "—";
  if (value === "—" || value.length <= maxLen) return { value, display: value };
  return { value, display: `${value.slice(0, maxLen)}…` };
}

function formatTruncatedCell(text, maxLen = VAULT_CELL_MAX_LEN) {
  const { value, display } = truncateVaultText(text, maxLen);
  return (
    <span className="qms-vault__truncated" title={value}>
      {display}
    </span>
  );
}

function isGuidLike(value) {
  return /^[0-9a-f-]{36}$/i.test(String(value || "").trim());
}

function resolveStaffDisplay(staffById, value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (isGuidLike(raw)) {
    return staffById.get(raw.toLowerCase())?.StaffName || raw;
  }
  return raw;
}

function QmsPublishDocuments() {
  const { userData, loading: userLoading } = useUserData();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const [allDocuments, setAllDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(true);
  const [staffRows, setStaffRows] = useState([]);
  const [refreshToken, setRefreshToken] = useState(0);
  const [workspaceModal, setWorkspaceModal] = useState({
    open: false,
    row: null,
    panel: "publish",
  });
  const [ownerFilter, setOwnerFilter] = useState([]);
  const [modifiedDateRange, setModifiedDateRange] = useState(QMS_EMPTY_DATE_RANGE);

  const userDataRef = useRef(userData);
  const showAlertRef = useRef(showAlert);
  userDataRef.current = userData;
  showAlertRef.current = showAlert;

  const staffId = userData?.StaffId || "";
  const staffById = useMemo(() => buildStaffByIdMap(staffRows), [staffRows]);
  const isSystemAdmin = userData?.Role === "System Administrator";
  const access = getQmsAccessState({
    userData,
    isSystemAdmin,
    permissionKey: screen?.permissionKey || "QMS_PUBLISH_DOCUMENTS",
    loading: userLoading,
  });

  const reportRows = useMemo(
    () => filterPublishReadyDocuments(allDocuments),
    [allDocuments],
  );

  const ownerFilterOptions = useMemo(() => {
    const map = new Map();
    reportRows.forEach((row) => {
      const raw = String(row.CreatedBy || "").trim();
      if (!raw) return;
      const key = raw.toLowerCase();
      if (map.has(key)) return;
      map.set(key, {
        value: key,
        label: resolveStaffDisplay(staffById, raw) || raw,
      });
    });
    return [...map.values()].sort((a, b) =>
      String(a.label).localeCompare(String(b.label)),
    );
  }, [reportRows, staffById]);

  const filteredReportRows = useMemo(() => {
    const selected = ownerFilter.length
      ? new Set(ownerFilter.map((value) => String(value).toLowerCase()))
      : null;
    return reportRows.filter((row) => {
      if (
        selected &&
        !selected.has(String(row.CreatedBy || "").trim().toLowerCase())
      ) {
        return false;
      }
      return matchesQmsDateRange(row?.SystemDateTime, modifiedDateRange);
    });
  }, [modifiedDateRange, ownerFilter, reportRows]);

  const loadDocuments = useCallback(async () => {
    const currentUser = userDataRef.current;
    if (!currentUser?.OrganizationId || !currentUser?.StaffId) return;

    setDocumentsLoading(true);
    try {
      const response = await fetchDocumentsForPublish(currentUser);
      if (!isApiSuccess(response)) {
        throw new Error(apiMessage(response) || "Could not load documents for publish.");
      }
      setAllDocuments(getPublishDocumentsList(response));
    } catch (error) {
      setAllDocuments([]);
      showAlertRef.current?.({
        title: "Error",
        message: error?.message || "Could not load documents for publish.",
        type: "danger",
      });
    } finally {
      setDocumentsLoading(false);
    }
  }, []);

  const handleRefresh = useCallback(() => {
    setRefreshToken((value) => value + 1);
  }, []);

  useEffect(() => {
    if (access.status !== "allowed" || !userData?.OrganizationId || !userData?.StaffId) {
      return undefined;
    }

    let cancelled = false;
    fetchStaffLookup(userData)
      .then((staff) => {
        if (!cancelled) setStaffRows(Array.isArray(staff) ? staff : []);
      })
      .catch(() => {
        if (!cancelled) setStaffRows([]);
      });

    return () => {
      cancelled = true;
    };
  }, [access.status, userData]);

  useEffect(() => {
    if (access.status !== "allowed" || !userData?.OrganizationId || !userData?.StaffId) {
      return undefined;
    }
    loadDocuments();
    return undefined;
  }, [access.status, userData?.OrganizationId, userData?.StaffId, refreshToken, loadDocuments]);

  const openDocument = useCallback((row, panel = "publish") => {
    if (!canOpenPublishWorkspace(row)) {
      showAlertRef.current?.({
        title: "Not ready to publish",
        message:
          String(row?.DocumentStage || "").trim() === "Approval Completed"
            ? "This document is checked out. Check it in before publishing."
            : "Only documents with stage Approval Completed can be published.",
        type: "warning",
      });
      return;
    }
    setWorkspaceModal({ open: true, row, panel });
  }, []);

  const closeDocument = useCallback(() => {
    setWorkspaceModal({ open: false, row: null, panel: "publish" });
  }, []);

  const renderReportCell = useCallback(
    (row, field) => {
      if (field === "DocumentId") {
        const docId = getVaultDocumentListId(row);
        return (
          <span className="qms-doc-lib__doc-id" title={docId}>
            {docId}
          </span>
        );
      }

      if (field === "DocumentTitle") {
        const name = getVaultDocumentDisplayName(row);
        const { value: fullName, display } = truncateVaultText(name || "Document");
        return (
          <button
            type="button"
            className="report-table__cell-link qms-doc-lib__doc-title-link qms-vault__doc-title-link"
            title={fullName}
            onClick={() => openDocument(row)}
          >
            {display}
          </button>
        );
      }

      if (field === "VersionNumber") {
        return formatTruncatedCell(row.VersionNumber ?? row.versionNumber ?? "—");
      }

      if (field === "DocumentStage") {
        const stage = resolveVaultDocumentStage(row.DocumentStage);
        const { value, display } = truncateVaultText(stage);
        return (
          <span
            className={`qms-vault__stage ${getVaultStageClass(stage)}`}
            title={value}
          >
            {display}
          </span>
        );
      }

      if (field === "CreatedBy") {
        const ownerName = resolveStaffDisplay(staffById, row.CreatedBy) || row.CreatedBy || "—";
        return (
          <QmsVaultStaffAvatar name={ownerName} title={ownerName} />
        );
      }

      return formatTruncatedCell(row[field]);
    },
    [openDocument, staffById],
  );

  const matchColumnSearch = useCallback((row, field, searchValue) => {
    const raw = String(searchValue || "").trim();
    if (!raw) return true;

    if (field === "DocumentId") {
      const listId = getVaultDocumentListId(row).toLowerCase();
      const term = raw.toLowerCase();
      const digits = term.replace(/\D/g, "");
      if (digits) {
        return listId.replace(/\D/g, "").includes(digits) || listId.includes(term);
      }
      return listId.includes(term);
    }

    if (field === "DocumentTitle") {
      return getVaultDocumentDisplayName(row).toLowerCase().includes(raw.toLowerCase());
    }

    if (field === "CreatedBy" || field === "SystemDateTime") {
      // Owner / Modified filters are handled outside columnSearches.
      return true;
    }

    return String(row[field] ?? "")
      .toLowerCase()
      .includes(raw.toLowerCase());
  }, [staffById]);

  const renderColumnSearch = useCallback(
    (field, options) => {
      if (field === "DocumentId") {
        if (options?.checkOnly) return true;
        return (
          <input
            type="search"
            className="report-table__column-search qms-doc-lib__column-filter"
            placeholder="Search ID"
            value={options?.value || ""}
            disabled={options?.disabled}
            onChange={(event) => options?.onChange?.(event.target.value)}
          />
        );
      }

      if (field === "CreatedBy") {
        if (options?.checkOnly) return true;
        return (
          <div className="qms-doc-lib__column-filter-multiselect">
            <BadgeMultiSelect
              options={ownerFilterOptions}
              value={ownerFilter}
              onChange={setOwnerFilter}
              emptyLabel="All owners"
              addLabel="+"
              popoverTitle="Filter by owner"
              popoverSubtitle="Select one or more owners"
              showSearch
              showSelectAll
              compactSummary
              disabled={options?.disabled}
            />
          </div>
        );
      }

      if (field === "SystemDateTime") {
        if (options?.checkOnly) return true;
        return (
          <div className="qms-doc-lib__column-date-filter">
            <DateRangeFilter
              value={modifiedDateRange}
              onChange={setModifiedDateRange}
              placeholder="Modified range"
              presetOptions={QMS_COLUMN_DATE_PRESETS}
              compact
            />
          </div>
        );
      }

      return null;
    },
    [modifiedDateRange, ownerFilter, ownerFilterOptions],
  );

  const layoutStorageKey = useMemo(
    () => (staffId ? `qms-publish-documents-layout-v2-${staffId}` : ""),
    [staffId],
  );

  const breadcrumbItems = useMemo(
    () => [{ label: "Home", path: "/dashboard" }, { label: screen.title }],
    [],
  );

  const breadcrumbActions = useMemo(
    () => [
      {
        icon: `fas fa-sync-alt${documentsLoading ? " fa-spin" : ""}`,
        label: "Refresh",
        onClick: handleRefresh,
      },
    ],
    [documentsLoading, handleRefresh],
  );

  const breadcrumbHintText =
    "Documents ready for publish appear below (stage: Approval Completed). Open a document to assign My Learning and Document Library access, then publish.";

  const breadcrumbHint = useMemo(
    () => (
      <div
        className="qms-publish-documents__crumb-alert"
        role="status"
        title={breadcrumbHintText}
      >
        <i className="fas fa-info-circle" aria-hidden="true" />
        <span>
          Documents ready for publish appear below (stage:{" "}
          <strong>Approval Completed</strong>). Open a document to assign My
          Learning and Document Library access, then publish.
        </span>
      </div>
    ),
    [],
  );

  if (access.status === "loading") return null;

  if (access.status === "denied") {
    return (
      <div className="user-mgmt-page qms-publish-documents qms-my-vault qms-document-library">
        <PageBreadcrumb items={breadcrumbItems} />
        <div className="alert alert-warning" role="alert">
          <strong>Access restricted.</strong> {access.message}
        </div>
      </div>
    );
  }

  return (
    <div className="user-mgmt-page qms-publish-documents qms-my-vault qms-document-library">
      <InfoAlert {...alertState} onClose={hideAlert} />

      <PageBreadcrumb
        items={breadcrumbItems}
        actions={breadcrumbActions}
        trailingExtras={breadcrumbHint}
      />

      <div className="qms-doc-lib__panel">
        <div className="qms-doc-lib__report-wrap">
          <ReportTable
            rows={filteredReportRows}
            fieldOptions={PUBLISH_FIELD_OPTIONS}
            defaultColumns={DEFAULT_PUBLISH_COLUMNS}
            getFieldLabel={getPublishFieldLabel}
            renderCell={renderReportCell}
            renderColumnSearch={renderColumnSearch}
            matchColumnSearch={matchColumnSearch}
            pageSizeOptions={[10, 25, 50, 100]}
            columnSearchMode="client"
            persistLayoutKey={layoutStorageKey}
            loading={documentsLoading}
            emptyMessage="No documents are ready to publish right now."
            emptyDescription=""
          />
        </div>
      </div>

      <QmsPublishWorkspaceModal
        open={workspaceModal.open}
        row={workspaceModal.row}
        userData={userData}
        activePanel={workspaceModal.panel}
        onPanelChange={(panel) =>
          setWorkspaceModal((current) => ({ ...current, panel }))
        }
        onClose={closeDocument}
        onNotify={showAlert}
        onPublished={() => {
          handleRefresh();
        }}
      />
    </div>
  );
}

export default QmsPublishDocuments;
